/**
 * The simpler onboarding cases and the builder for a newly invited supplier.
 * They use the same data model as the Kestrel case, with fewer fields.
 */
import { daysAgo, fmtDate } from '../../data/dates';
import { getSupplier } from '../../data/suppliers';
import type { CategoryId, PersonId, ProcurementType } from '../../data/types';
import { getPerson, PERSONA_ID } from '../../data/company';
import { buildKestrelCase } from './onboarding-kestrel';
import type {
  ExtractedField,
  OnboardingCase,
  OnboardingStepKey,
  SourceRef,
  StepContent,
  StepState,
  StepStatus,
} from './onboarding-types';
import { emptyContent } from './onboarding-types';

const MINUTES_PER_DAY = 24 * 60;

function doc(name: string, confidence: number): SourceRef {
  return { name, kind: 'document', confidence };
}

function lookup(name: string, confidence = 100): SourceRef {
  return { name, kind: 'lookup', confidence };
}

function field(
  label: string,
  value: string,
  source: SourceRef,
): ExtractedField {
  return { label, value, source };
}

function step(status: StepStatus, content: StepContent): StepState {
  return { status, content };
}

function summaryOnly(status: StepStatus, summary: string): StepState {
  return step(status, emptyContent(summary));
}

// ---------------------------------------------------------------------------
// ONB-0091: Hanover Leuchten GmbH, a bidder on RFP-2026-014
// ---------------------------------------------------------------------------

function buildHanoverCase(): OnboardingCase {
  const supplier = getSupplier('sup-hanover');
  const bidPackage = 'Hanover_RFP-2026-014_Bid_Package.pdf';
  const registry = 'Handelsregister_Extract_Hanover.pdf';
  return {
    id: 'ONB-0091',
    supplierName: supplier.name,
    supplierId: 'sup-hanover',
    type: 'direct',
    categoryId: 'cat-lighting',
    purpose: 'Bidder on RFP-2026-014 (Pillar Pro LED high-bays, 2027 to 2029)',
    requestedBy: 'sarah-chen',
    stage: 'Risk review',
    progress: 52,
    riskLevel: supplier.riskLevel,
    riskScore: supplier.riskScore,
    aiSummary: {
      title: 'Risk review in progress: screening runs next',
      body: 'The profile and credit check are done and the bank confirmation letter is in. The agent is running sanctions, government-official and negative-news screening now, and a bank callback is scheduled. Hanover cannot be awarded in RFP-2026-014 until the risk review closes.',
      tone: 'neutral',
    },
    riskDimensions: [
      { label: 'Financial', score: 24 },
      { label: 'Legal and compliance', score: null },
      { label: 'Quality and regulatory', score: null },
      { label: 'Sustainability', score: 29 },
      { label: 'Supply continuity', score: 25 },
    ],
    openedDaysAgo: 11,
    waitingOn: 'Agent',
    waitingDetail: 'Sanctions and negative-news screening',
    contact: {
      name: supplier.primaryContact.name,
      title: supplier.primaryContact.title,
    },
    materialId: 'MAT-LED',
    sourcingEventId: 'RFP-2026-014',
    steps: {
      profile: step('done', {
        summary:
          'Profile pre-filled from the bid package and the commercial register extract.',
        fieldGroups: [
          {
            fields: [
              field('Legal name', supplier.name, doc(registry, 99)),
              field(
                'Registered office',
                `${supplier.hq.city}, ${supplier.hq.country}`,
                doc(registry, 98),
              ),
              field(
                'Commercial register',
                'HRB 215683, Amtsgericht Hannover',
                doc(registry, 97),
              ),
              field(
                'Global company number (D-U-N-S)',
                supplier.duns,
                lookup('Dun & Bradstreet'),
              ),
              field(
                'Primary contact',
                `${supplier.primaryContact.name}, ${supplier.primaryContact.title}`,
                doc(bidPackage, 96),
              ),
            ],
          },
        ],
        checkGroups: [],
        documents: [
          { name: registry, state: 'received', daysAgo: 9, fieldsExtracted: 8 },
          {
            name: bidPackage,
            state: 'received',
            daysAgo: 10,
            fieldsExtracted: 14,
          },
        ],
      }),
      'tax-banking': step('attention', {
        summary:
          'Bank confirmation letter received. Ownership verification by callback is scheduled.',
        fieldGroups: [
          {
            fields: [
              field(
                'Bank',
                supplier.bank.bankName,
                doc('Hanover_Bank_Confirmation.pdf', 97),
              ),
              field(
                'Account',
                supplier.bank.accountMasked,
                doc('Hanover_Bank_Confirmation.pdf', 98),
              ),
              field('VAT number', 'DE 318 204 957', doc(registry, 96)),
            ],
          },
        ],
        checkGroups: [
          {
            checks: [
              {
                label: 'Bank account format valid',
                outcome: 'pass',
                checkedDaysAgo: 6,
                detail: 'Country DE, 22 characters, checksum correct.',
              },
              {
                label: 'Account name matches legal name',
                outcome: 'pass',
                checkedDaysAgo: 6,
                detail: 'Matches the bank confirmation letter.',
              },
              {
                label: 'Callback verification',
                outcome: 'pending',
                detail:
                  'Scheduled with the finance contact listed in the commercial register.',
              },
            ],
          },
        ],
        documents: [
          {
            name: 'Hanover_Bank_Confirmation.pdf',
            state: 'received',
            daysAgo: 6,
            fieldsExtracted: 6,
          },
        ],
      }),
      financial: step('done', {
        summary:
          'Credit score 75 of 100. Revenue and payment behavior within the limit for a bidder.',
        fieldGroups: [
          {
            fields: [
              field(
                'Credit score',
                `${supplier.creditScore} / 100, low risk`,
                lookup('Credit bureau report'),
              ),
              field(
                'Payment record',
                'Pays within terms',
                lookup('Credit bureau report'),
              ),
            ],
          },
        ],
        checkGroups: [],
        documents: [],
      }),
      legal: summaryOnly(
        'pending',
        'Sanctions, government-official, negative-news and lawsuit screening is running. Results are expected within the hour.',
      ),
      quality: step('pending', {
        summary:
          'Certificates received. The supplier quality questionnaire has been sent and the UL and DLC certificates for the high-bay light are requested.',
        fieldGroups: [
          {
            fields: supplier.certifications.map((cert) =>
              field('Certification', cert, doc('Hanover_Certificates.pdf', 95)),
            ),
          },
        ],
        checkGroups: [],
        documents: [
          {
            name: 'Hanover_Certificates.pdf',
            state: 'received',
            daysAgo: 8,
            fieldsExtracted: 6,
          },
          {
            name: 'Supplier quality questionnaire',
            state: 'requested',
            daysAgo: 3,
          },
          {
            name: 'UL and DLC certificates for the high-bay light',
            state: 'requested',
            daysAgo: 3,
          },
        ],
      }),
      'insurance-esg': step('pending', {
        summary:
          'Sustainability score found. Certificate of insurance still to be requested.',
        fieldGroups: [
          {
            fields: [
              field(
                'Sustainability score (EcoVadis)',
                `${supplier.ecoVadis ?? 0} / 100`,
                lookup('EcoVadis scorecard'),
              ),
            ],
          },
        ],
        checkGroups: [],
        documents: [{ name: 'Certificate of insurance', state: 'expected' }],
      }),
      agreements: summaryOnly(
        'pending',
        'No agreement is needed until the RFP-2026-014 award. A supply agreement will be generated from the template at award.',
      ),
      approvals: summaryOnly(
        'pending',
        'Approvals start once the risk review is complete.',
      ),
    },
    agentLog: [
      {
        minutesAgo: 95,
        text: 'Started sanctions, government-official and negative-news screening',
        detail:
          '4 sanctions lists, government officials, negative news and lawsuits.',
      },
      {
        minutesAgo: 1 * MINUTES_PER_DAY + 200,
        text: 'Extracted 34 fields from 4 documents',
      },
      {
        minutesAgo: 6 * MINUTES_PER_DAY,
        text: 'Requested the bank confirmation letter and certificates',
      },
      {
        minutesAgo: 10 * MINUTES_PER_DAY,
        text: 'Pre-filled the profile from the RFP-2026-014 bid package',
      },
      {
        minutesAgo: 11 * MINUTES_PER_DAY,
        text: 'Created the case from the RFP-2026-014 bidder list',
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// ONB-0093: Northwind Safety Training, waiting on the supplier
// ---------------------------------------------------------------------------

function buildNorthwindCase(): OnboardingCase {
  const registration = 'Northwind_Portal_Registration';
  const requester: PersonId = 'marcus-hale';
  return {
    id: 'ONB-0093',
    supplierName: 'Northwind Safety Training',
    type: 'indirect',
    categoryId: 'cat-prof-services',
    purpose: 'Workplace electrical safety training for DC and branch staff',
    requestedBy: requester,
    stage: 'Waiting on supplier',
    progress: 30,
    riskLevel: 'low',
    riskScore: 21,
    aiSummary: {
      title: 'Waiting on the supplier for a tax form and a bank letter',
      body: 'Northwind registered in the portal 8 days ago and nothing else can run until the tax form arrives, because the credit check and screening need the tax ID. Two reminders have gone out. A third goes out tomorrow and copies the requester.',
      tone: 'warning',
    },
    riskDimensions: [
      { label: 'Financial', score: null },
      { label: 'Legal and compliance', score: null },
      { label: 'Quality and regulatory', score: null },
      { label: 'Sustainability', score: null },
      { label: 'Supply continuity', score: 21 },
    ],
    openedDaysAgo: 8,
    waitingOn: 'Supplier',
    waitingDetail: 'Tax form and bank letter',
    contact: {
      name: 'Carla Mendez',
      title: 'Operations Manager',
      email: 'accounts@northwind-safety.example',
    },
    steps: {
      profile: step('done', {
        summary:
          'The supplier self-registered in the portal. Profile verified against the state registry.',
        fieldGroups: [
          {
            fields: [
              field('Legal name', 'Northwind Safety Training LLC', {
                name: registration,
                kind: 'portal',
                confidence: 100,
              }),
              field('Address', '4410 Commerce Street, Fort Worth, TX 76107', {
                name: registration,
                kind: 'portal',
                confidence: 100,
              }),
              field(
                'Business type',
                'Limited liability company, 14 employees',
                { name: registration, kind: 'portal', confidence: 100 },
              ),
              field(
                'Registry status',
                'Active and in good standing',
                lookup('Texas Secretary of State'),
              ),
            ],
          },
        ],
        checkGroups: [],
        documents: [],
      }),
      'tax-banking': step('waiting', {
        summary:
          'Waiting on the supplier for a tax form and a bank confirmation letter. Two reminders have been sent.',
        fieldGroups: [],
        checkGroups: [],
        documents: [
          { name: 'Tax form', state: 'requested', daysAgo: 6 },
          { name: 'Bank confirmation letter', state: 'requested', daysAgo: 6 },
        ],
      }),
      financial: summaryOnly(
        'pending',
        'The credit check runs once the tax form provides the tax ID.',
      ),
      legal: summaryOnly(
        'pending',
        'Screening starts after the tax ID is confirmed.',
      ),
      quality: summaryOnly(
        'na',
        'Not required for an indirect services supplier.',
      ),
      'insurance-esg': step('pending', {
        summary:
          'A professional liability certificate will be requested with the tax form follow-up.',
        fieldGroups: [],
        checkGroups: [],
        documents: [
          { name: 'Professional liability certificate', state: 'expected' },
        ],
      }),
      agreements: summaryOnly(
        'pending',
        'A services agreement will be generated from the Pillar template after the screening.',
      ),
      approvals: summaryOnly(
        'pending',
        'Approvals start once the tax and banking documents are in.',
      ),
    },
    agentLog: [
      {
        minutesAgo: 1 * MINUTES_PER_DAY + 30,
        text: 'Sent a second reminder for the tax form and bank letter',
        detail: `Copied the requester, ${getPerson(requester).name}.`,
      },
      {
        minutesAgo: 4 * MINUTES_PER_DAY,
        text: 'Sent a first reminder for the tax form and bank letter',
      },
      {
        minutesAgo: 6 * MINUTES_PER_DAY,
        text: 'Requested the tax form and the bank confirmation letter',
      },
      {
        minutesAgo: 8 * MINUTES_PER_DAY,
        text: 'Verified the portal registration against the state registry',
        detail: 'Active and in good standing.',
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// ONB-0079: Clearpath Shop Supplies, completed
// ---------------------------------------------------------------------------

function buildClearpathCase(): OnboardingCase {
  const done = (summary: string): StepState => summaryOnly('done', summary);
  return {
    id: 'ONB-0079',
    supplierName: 'Clearpath Shop Supplies',
    type: 'indirect',
    categoryId: 'cat-facilities',
    purpose: 'Consumables for the Dallas panel shop (catalog)',
    requestedBy: 'marcus-hale',
    stage: 'Completed',
    progress: 100,
    riskLevel: 'low',
    riskScore: 12,
    riskTier: 'Level 3: indirect, low spend',
    aiSummary: {
      title: 'Onboarding complete: supplier 100471 is active',
      body: 'All screenings were clear and the supplier was created in the ERP with Net 45 terms. The catalog is loaded for the Dallas panel shop and request REQ-1055 was released.',
      tone: 'success',
    },
    riskDimensions: [
      { label: 'Financial', score: 10 },
      { label: 'Legal and compliance', score: 8 },
      { label: 'Quality and regulatory', score: null },
      { label: 'Sustainability', score: 15 },
      { label: 'Supply continuity', score: 14 },
    ],
    openedDaysAgo: 19,
    completedDaysAgo: 5,
    waitingOn: 'None',
    contact: {
      name: 'Dana Brennan',
      title: 'Account Manager',
      email: 'dana.brennan@clearpath-shop.example',
    },
    supplierMasterId: '100471',
    paymentTerms: 'Net 45',
    steps: {
      profile: done(
        'Profile verified against the registry. Supplier created in the ERP as 100471.',
      ),
      'tax-banking': done(
        'Tax form received and bank account ownership verified by callback.',
      ),
      financial: done('Credit score 78 of 100, low risk.'),
      legal: done(
        'Sanctions, government-official, negative-news and lawsuit screening clear.',
      ),
      quality: summaryOnly('na', 'Not required for catalog consumables.'),
      'insurance-esg': done(
        'Certificate of insurance on file. No sustainability rating required below the spend threshold.',
      ),
      agreements: done('Catalog order terms accepted, Net 45.'),
      approvals: step('done', {
        summary: `Approved by Procurement on ${fmtDate(daysAgo(5))}.`,
        fieldGroups: [],
        checkGroups: [],
        documents: [],
        approvals: [
          {
            role: 'Finance, bank details',
            person: 'tom-gallagher',
            status: 'approved',
            decidedDaysAgo: 6,
            note: 'Bank account ownership verified.',
          },
          {
            role: 'Procurement',
            person: 'daniel-reyes',
            status: 'approved',
            decidedDaysAgo: 5,
            note: 'Supplier created as 100471.',
          },
        ],
      }),
    },
    agentLog: [
      {
        minutesAgo: 5 * MINUTES_PER_DAY,
        text: 'Created supplier 100471 and synced it to the ERP',
        detail: 'Payment terms Net 45. Catalog loaded for the panel shop.',
      },
      {
        minutesAgo: 6 * MINUTES_PER_DAY,
        text: 'Routed approvals to Finance and Procurement',
      },
      {
        minutesAgo: 9 * MINUTES_PER_DAY,
        text: 'Ran 7 screenings, all clear',
      },
      {
        minutesAgo: 14 * MINUTES_PER_DAY,
        text: 'Extracted 18 fields from 3 documents',
      },
      {
        minutesAgo: 19 * MINUTES_PER_DAY,
        text: 'Opened the case from request REQ-1055',
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// New invite
// ---------------------------------------------------------------------------

export interface InviteInput {
  id: string;
  name: string;
  email: string;
  type: ProcurementType;
  categoryId: CategoryId;
}

function websiteFromEmail(email: string): string {
  const domain = email.split('@')[1];
  return domain ? `www.${domain}` : 'Not found';
}

/** A case created from the invite dialog: pre-filled from public sources, waiting on the supplier. */
export function buildInvitedCase(input: InviteInput): OnboardingCase {
  const notStarted = (): StepState =>
    summaryOnly(
      'pending',
      'Starts after the supplier completes the portal form.',
    );
  const profile = step('attention', {
    summary:
      'Pre-filled from public sources. The supplier confirms or corrects each value in the portal.',
    fieldGroups: [
      {
        fields: [
          field('Legal name', input.name, {
            name: 'Invite form',
            kind: 'portal',
            confidence: 100,
          }),
          field('Invite sent to', input.email, {
            name: 'Invite form',
            kind: 'portal',
            confidence: 100,
          }),
          field(
            'Website',
            websiteFromEmail(input.email),
            lookup('Email domain', 70),
          ),
          field(
            'Registry record',
            'Candidate match found. Supplier to confirm the registration number.',
            lookup('Company registry', 68),
          ),
        ],
      },
    ],
    checkGroups: [],
    documents: [],
  });
  const taxBanking = step('waiting', {
    summary:
      'Waiting on the supplier for tax forms and bank details through the portal.',
    fieldGroups: [],
    checkGroups: [],
    documents: [
      { name: 'Tax form', state: 'requested', daysAgo: 0 },
      { name: 'Bank confirmation letter', state: 'requested', daysAgo: 0 },
    ],
  });
  const steps: Record<OnboardingStepKey, StepState> = {
    profile,
    'tax-banking': taxBanking,
    financial: notStarted(),
    legal: notStarted(),
    quality:
      input.type === 'indirect'
        ? summaryOnly('na', 'Not required for an indirect supplier.')
        : notStarted(),
    'insurance-esg': notStarted(),
    agreements: notStarted(),
    approvals: notStarted(),
  };
  return {
    id: input.id,
    supplierName: input.name,
    type: input.type,
    categoryId: input.categoryId,
    purpose: 'Invited from the supplier queue',
    requestedBy: PERSONA_ID,
    stage: 'Waiting on supplier',
    progress: 10,
    riskLevel: null,
    riskScore: null,
    aiSummary: {
      title: 'Invite sent: waiting on the supplier',
      body: 'The profile is pre-filled from public sources and needs the supplier to confirm it. Tax forms and bank details come next, then screening starts automatically.',
      tone: 'neutral',
    },
    riskDimensions: [
      { label: 'Financial', score: null },
      { label: 'Legal and compliance', score: null },
      { label: 'Quality and regulatory', score: null },
      { label: 'Sustainability', score: null },
      { label: 'Supply continuity', score: null },
    ],
    openedDaysAgo: 0,
    waitingOn: 'Supplier',
    waitingDetail: 'Portal form',
    contact: {
      name: input.email,
      title: 'Portal invite sent',
      email: input.email,
    },
    steps,
    agentLog: [
      {
        minutesAgo: 0,
        text: `Sent the portal invite to ${input.email}`,
        detail: 'The supplier completes the profile, tax and bank forms.',
      },
      {
        minutesAgo: 1,
        text: 'Pre-filled the profile from public sources',
        detail: '2 of 4 values matched with high confidence.',
      },
      {
        minutesAgo: 2,
        text: 'Looked up the company registry',
        detail: 'One candidate record found.',
      },
    ],
  };
}

export function buildSeedCases(): OnboardingCase[] {
  return [
    buildKestrelCase(),
    buildHanoverCase(),
    buildNorthwindCase(),
    buildClearpathCase(),
  ];
}
